import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import FsiPicker from '../FsiPicker/FsiPicker'
import type { FsiPickerOptions } from '../FsiPicker/FsiPicker'
import type { TreeNode } from '../FsiPickerNode'
import { useServiceContext } from '../../root'
import { usePageContext } from '../../contexts/page-state'
import { UserRole } from '../../types/access'

interface StudySiteSelectFolderFieldProps {
  isToggled?: boolean
  setIsShown?: (shown: boolean) => void
}

interface FolderPickerOptions {
  showRC?: boolean
  showSystem?: boolean
  isSiteUser?: boolean
  isStudyUser?: boolean
}

const StudySiteSelectFolderField: React.FC<StudySiteSelectFolderFieldProps> = ({ isToggled = false, setIsShown }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const serviceContext = useServiceContext()
  const pageContext = usePageContext()

  // Draggable modal state
  const [shown, setShown] = useState(isToggled)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 110 })
  // Ref for the ALL_FOLDERS_DOCUMENTS button
  const triggerButtonRef = useRef<HTMLAnchorElement | null>(null)

  // Mouse event handlers for drag
  useEffect(() => {
    if (!shown || !dragging) return

    let rafId: number | null = null

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()

      // Cancel previous animation frame if it exists
      if (rafId) {
        cancelAnimationFrame(rafId)
      }

      // Use requestAnimationFrame for smooth updates
      rafId = requestAnimationFrame(() => {
        // Calculate how much the mouse has moved from drag start
        const deltaX = e.clientX - dragStart.x
        const deltaY = e.clientY - dragStart.y

        // New position = initial position + delta
        const newX = initialPosition.x + deltaX
        const newY = initialPosition.y + deltaY

        // Apply boundary constraints
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const minTop = 60
        const maxTop = viewportHeight - 100
        const minLeft = 0
        const maxLeft = viewportWidth - 300 // Approximate modal width

        const constrainedX = Math.max(minLeft, Math.min(maxLeft, newX))
        const constrainedY = Math.max(minTop, Math.min(maxTop, newY))

        setPosition({
          x: constrainedX,
          y: constrainedY
        })
      })
    }

    const handleMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      setDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: false })
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging, dragStart, initialPosition, shown])

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Store current mouse position and current modal position
    setDragStart({
      x: e.clientX,
      y: e.clientY
    })
    setInitialPosition(position)
    setDragging(true)
  }

  // Get user role from auth service
  const userRole = serviceContext.service.auth.getRole?.() || ''

  // When opening the modal, set its position relative to the trigger button
  const toggleSelectFolder = () => {
    setShown((prev) => !prev)
  }

  // Always align modal with nav-link when shown
  useLayoutEffect(() => {
    if (shown && triggerButtonRef.current) {
      const rect = triggerButtonRef.current.getBoundingClientRect()
      const modalX = rect.left
      const modalY = rect.bottom + 8 // 8px gap below button
      setPosition({ x: modalX, y: modalY })
      setInitialPosition({ x: modalX, y: modalY })
    }
  }, [shown])

  const closeModal = () => {
    setShown(false)
    if (setIsShown) {
      setIsShown(false)
    }
  }

  /**
   * Get folder picker options based on user role
   * Determines what folder types the user can see in the picker
   */
  const getFolderPickerOptions = (role: string): FolderPickerOptions => {
    switch (role) {
      case UserRole.ADMIN:
        return { showRC: true, showSystem: true }
      case UserRole.AUDITOR:
        return { showRC: true, showSystem: true }
      case UserRole.CONTENT_ADMIN:
        return { showRC: true, showSystem: true }
      case UserRole.ETMF_MANAGER:
        return { showRC: true, showSystem: true }
      case UserRole.ETMF_SITE_USER:
        return { showRC: false, showSystem: false, isSiteUser: true }
      case UserRole.ETMF_SPECIALIST:
        return { showRC: true, showSystem: true, isSiteUser: true, isStudyUser: true }
      case UserRole.ETMF_SPONSOR:
        return { showRC: true, showSystem: true, isSiteUser: true, isStudyUser: true }
      case UserRole.ETMF_STUDY_USER:
        return { showRC: false, showSystem: false, isStudyUser: true }
      case UserRole.INSPECTOR:
        return { showRC: true, showSystem: false }
      case UserRole.POWER_USER:
        return { showRC: true, showSystem: true }
      case UserRole.RCR_USER:
        return { showRC: true, showSystem: true }
      case UserRole.USER:
        return { showRC: true, showSystem: true }
      default:
        return { showRC: false, showSystem: false }
    }
  }

  /**
   * Handle folder selection from FsiPicker
   * Navigate to the documents browser with selected folder
   */
  const handleFolderSelect = (selectedIds: (number | string)[] | TreeNode, baseNodes?: TreeNode[] | undefined) => {
    // Handle both array of IDs (checkbox mode) and single node (radio mode)
    if (Array.isArray(selectedIds)) {
      if (baseNodes && baseNodes.length > 0 && selectedIds.length > 0) {
        const folder = baseNodes[0]
        // Navigate to browser with folderId as path parameter (matches route: /browser/:folderId)
        // Keep modal open after navigation (matches old app behavior)
        navigate(`/browser/${folder.id}?page=1&type=${folder.systemFolderType || 'REGULATED_CONTENT'}`)
      }
    } else {
      // Single node selected (not in checkbox mode)
      const folder = selectedIds as TreeNode
      // Keep modal open after navigation (matches old app behavior)
      navigate(`/browser/${folder.id}?page=1&type=${folder.systemFolderType || 'REGULATED_CONTENT'}`)
    }
  }

  const folderPickerOptions = getFolderPickerOptions(userRole)

  return (
    <>
      {/* Trigger menu item - only show for staging uploaders */}
      {pageContext.isStagingUploader && (
        <li className={`side-nav-item${shown ? ' active' : ''}`}>
          <a onClick={toggleSelectFolder} className="" ref={triggerButtonRef}>
            <span className="fs14">{t('ALL_FOLDERS_DOCUMENTS', 'All Folders & Documents')}</span>
          </a>
        </li>
      )}

      {/* Folder selector modal */}
      {shown && (
        <div
          className="select-folder-wrapper"
          key="select-folder"
          style={{
            left: position.x,
            top: position.y,
            position: 'absolute',
            zIndex: 1000,
            margin: 0
          }}
        >
          <div className="select-folder-custom-style select-folder select-folder-container active">
            <div
              className="header selectfolder"
              onMouseDown={onHeaderMouseDown}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              style={{ cursor: 'move', userSelect: 'none' }}
            >
              <span className="fs14">{t('SELECT_FOLDER', 'Select Folder')}</span>
              <i
                className="close-modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setShown(false)
                }}
              ></i>
            </div>
            <FsiPicker
              options={
                {
                  selector: 'folder',
                  ...folderPickerOptions
                } as Partial<FsiPickerOptions>
              }
              onSelect={handleFolderSelect}
              isNodeSelectable={() => true}
              showAddNewFolderModal={false}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default StudySiteSelectFolderField
